import { useState, useEffect } from 'react';
import branchService from '../services/branchService';

const BRANCH_CACHE_STORAGE_KEY = 'branch_cache';

const loadPersistedBranch = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(BRANCH_CACHE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to read persisted branch cache:', error);
    return null;
  }
};

// Cache branch data to avoid repeated API calls
let cachedBranch = loadPersistedBranch();
let cachePromise = null;
const subscribers = new Set();

const normalizeBranch = (branch) => {
  if (!branch) return branch;

  return {
    ...branch,
    posDesignTheme: Number(branch.posDesignTheme) === 2 ? 2 : 1,
  };
};

const notifySubscribers = (branch) => {
  subscribers.forEach((subscriber) => subscriber(branch));
};

export function setBranchCache(branch) {
  cachedBranch = normalizeBranch(branch);
  cachePromise = null;

  if (typeof window !== 'undefined') {
    try {
      if (cachedBranch) {
        window.localStorage.setItem(BRANCH_CACHE_STORAGE_KEY, JSON.stringify(cachedBranch));
      } else {
        window.localStorage.removeItem(BRANCH_CACHE_STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to persist branch cache:', error);
    }
  }

  notifySubscribers(cachedBranch);
}

/**
 * Hook to fetch and cache branch/restaurant data
 * Uses singleton pattern to avoid multiple API calls
 *
 * @returns {Object} { branch, loading, error, refetch }
 */
export default function useBranch() {
  const [branch, setBranch] = useState(cachedBranch);
  const [loading, setLoading] = useState(!cachedBranch);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCacheUpdate = (nextBranch) => {
      setBranch(nextBranch);
      setLoading(false);
      if (nextBranch) {
        setError(null);
      }
    };

    subscribers.add(handleCacheUpdate);

    const fetchBranch = async () => {
      // If already cached, use it
      if (cachedBranch) {
        setBranch(cachedBranch);
        setLoading(false);
        return;
      }

      // If a fetch is already in progress, wait for it
      if (cachePromise) {
        try {
          const data = await cachePromise;
          setBranch(data);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
        return;
      }

      // Start new fetch
      setLoading(true);
      cachePromise = branchService.getCurrentBranch();

      try {
        const data = await cachePromise;
        setBranchCache(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch branch:', err);
        setError(err.message);
        setBranch(cachedBranch);
      } finally {
        setLoading(false);
        cachePromise = null;
      }
    };

    fetchBranch();

    return () => {
      subscribers.delete(handleCacheUpdate);
    };
  }, []);

  // Force refetch (clears cache)
  const refetch = async () => {
    cachedBranch = null;
    cachePromise = null;
    setLoading(true);

    try {
      const data = await branchService.getCurrentBranch();
      setBranchCache(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Parse hot items from comma-separated string to array
  const hotItems = branch?.hotItems
    ? branch.hotItems.split(',').map(item => item.trim()).filter(Boolean)
    : [];

  return {
    branch,
    restaurantName: branch?.name || 'Restaurant',
    hotItems,
    timezone: branch?.timezone || 'Asia/Kolkata',
    posDesignTheme: branch?.posDesignTheme || 1,
    loading,
    error,
    refetch
  };
}

/**
 * Clear branch cache (call on logout)
 */
export function clearBranchCache() {
  cachedBranch = null;
  cachePromise = null;
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(BRANCH_CACHE_STORAGE_KEY);
  }
  notifySubscribers(null);
}
